import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import WeatherWidget from '../components/WeatherWidget';
import { toast } from 'sonner';
import { 
  Plus, 
  Wifi, 
  WifiOff, 
  Gauge, 
  Clock, 
  Settings,
  Power,
  ChevronRight,
  RefreshCw,
  Battery,
  Cloud,
  Map
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [newDevice, setNewDevice] = useState({ serial_number: '', name: '', location: '' });
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === 'device_update') {
      setDevices(prev => 
        prev.map(d => d.id === message.device.id ? message.device : d)
      );
    }
  }, []);

  const { isConnected } = useWebSocket(token, handleWebSocketMessage);

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API}/devices`);
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const syncFromCloud = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/fac/devices/sync`);
      toast.success(`${response.data.message}`);
      fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from FAC Cloud');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleAddDevice = async (e) => {
    e.preventDefault();
    setAdding(true);

    try {
      const response = await axios.post(`${API}/devices`, newDevice);
      setDevices([...devices, response.data]);
      toast.success('Device added successfully! / 設備新增成功！');
      setShowAddDevice(false);
      setNewDevice({ serial_number: '', name: '', location: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add device');
    } finally {
      setAdding(false);
    }
  };

  const getStatusColor = (status) => {
    return status === 'online' ? 'text-fac-green' : 'text-fac-red';
  };

  const getValveIcon = (state) => {
    return state === 'open' ? (
      <div className="w-3 h-3 rounded-full bg-fac-green animate-pulse" />
    ) : (
      <div className="w-3 h-3 rounded-full bg-muted-foreground" />
    );
  };

  const formatLastSeen = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-TW', { 
      hour: '2-digit', 
      minute: '2-digit',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-fac-dark pt-16 pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-3xl text-white uppercase" data-testid="dashboard-title">
              {t('dashboard_title')}
            </h1>
            <p className="text-muted-foreground font-chinese">
              Welcome, {user?.name} / 歡迎, {user?.name}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 text-sm ${isConnected ? 'text-fac-green' : 'text-fac-red'}`}>
              {isConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span>{isConnected ? 'Live' : 'Offline'}</span>
            </div>
            <Button
              onClick={syncFromCloud}
              disabled={syncing}
              variant="outline"
              className="border-fac-blue text-fac-blue hover:bg-fac-blue/10"
              data-testid="sync-cloud-btn"
            >
              {syncing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Cloud className="w-4 h-4 mr-2" />
              )}
              <span className="hidden md:inline">{syncing ? 'Syncing...' : 'Sync Cloud'}</span>
            </Button>
            <Link to="/app/map">
              <Button
                variant="outline"
                className="border-fac-yellow text-fac-yellow hover:bg-fac-yellow/10"
                data-testid="map-view-btn"
              >
                <Map className="w-4 h-4 mr-2" />
                <span className="hidden md:inline">Map</span>
              </Button>
            </Link>
            <Button
              onClick={() => setShowAddDevice(true)}
              className="btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim"
              data-testid="add-device-btn"
            >
              <span className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                {t('dashboard_add_device')}
              </span>
            </Button>
          </div>
        </div>

        {/* Weather Widget */}
        <WeatherWidget className="mb-6" />

        {/* Devices Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="bg-fac-card border-border">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                    <div className="h-8 bg-muted rounded" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : devices.length === 0 ? (
          <Card className="bg-fac-card border-border">
            <CardContent className="p-12 text-center">
              <Power className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl text-muted-foreground font-chinese">
                {t('dashboard_no_devices')}
              </p>
              <Button
                onClick={() => setShowAddDevice(true)}
                className="mt-6 bg-fac-green text-black font-bold"
                data-testid="add-first-device-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('dashboard_add_device')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="devices-grid">
            {devices.map((device) => (
              <Link key={device.id} to={`/app/device/${device.id}`}>
                <Card 
                  className={`bg-fac-card border-border hover:border-fac-green transition-all duration-300 cursor-pointer ${
                    device.is_locked ? 'opacity-60' : ''
                  }`}
                  data-testid={`device-card-${device.id}`}
                >
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg text-white truncate">
                          {device.name}
                        </h3>
                        <p className="text-sm text-muted-foreground font-mono">
                          {device.serial_number}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 ${getStatusColor(device.status)}`}>
                        {device.status === 'online' ? (
                          <Wifi className="w-5 h-5" />
                        ) : (
                          <WifiOff className="w-5 h-5" />
                        )}
                        <span className="text-sm uppercase font-bold">
                          {device.status === 'online' ? t('device_online') : t('device_offline')}
                        </span>
                      </div>
                    </div>

                    {/* Status Row */}
                    <div className="flex items-center gap-4 mb-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        {getValveIcon(device.valve_state)}
                        <span className="text-sm text-white capitalize font-chinese">
                          {device.valve_state === 'open' ? '開啟 Open' : '關閉 Closed'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-fac-blue" />
                        <span className="text-sm text-white">
                          {device.pressure} {t('bar')}
                        </span>
                      </div>
                      {device.battery !== undefined && (
                        <div className="flex items-center gap-2">
                          <Battery className={`w-4 h-4 ${device.battery < 20 ? 'text-fac-red' : device.battery < 50 ? 'text-fac-yellow' : 'text-fac-green'}`} />
                          <span className={`text-sm ${device.battery < 20 ? 'text-fac-red' : 'text-white'}`}>
                            {device.battery}%
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Clock className="w-4 h-4" />
                        <span className="font-chinese">{formatLastSeen(device.last_seen)}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>

                    {/* Lock indicator */}
                    {device.is_locked && (
                      <div className="mt-3 py-2 px-3 bg-fac-red/10 rounded text-fac-red text-sm text-center font-chinese">
                        Device Locked / 設備已鎖定
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Add Device Dialog */}
      <Dialog open={showAddDevice} onOpenChange={setShowAddDevice}>
        <DialogContent className="bg-fac-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-white uppercase">
              {t('dashboard_add_device')}
              <span className="block text-fac-green font-chinese text-base mt-1">新增設備</span>
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddDevice} className="space-y-4" data-testid="add-device-form">
            <div>
              <Label className="text-white font-chinese">Serial Number / 序號</Label>
              <Input
                required
                value={newDevice.serial_number}
                onChange={(e) => setNewDevice({ ...newDevice, serial_number: e.target.value })}
                className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                placeholder="FAC-XXX-XX"
                data-testid="device-serial-input"
              />
            </div>
            
            <div>
              <Label className="text-white font-chinese">Device Name / 設備名稱</Label>
              <Input
                required
                value={newDevice.name}
                onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
                className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                placeholder="North Field Valve / 北田區閥門"
                data-testid="device-name-input"
              />
            </div>
            
            <div>
              <Label className="text-white font-chinese">Location / 位置 (Optional)</Label>
              <Input
                value={newDevice.location}
                onChange={(e) => setNewDevice({ ...newDevice, location: e.target.value })}
                className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                placeholder="Farm Location / 農場位置"
                data-testid="device-location-input"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDevice(false)}
                className="border-border text-white"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={adding}
                className="bg-fac-green text-black font-bold"
                data-testid="add-device-submit-btn"
              >
                {adding ? t('loading') : t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
